import * as joda from "@js-joda/core";
import { checkAllCasesHandled } from "trader.ts/utils";
import { getFirstWeekdayFrom, timerangesOverlap } from "./time";

type period = {
  firstday: joda.LocalDate;
  lastday: joda.LocalDate;
  holidays: {
    description: string;
    firstday: joda.LocalDate;
    lastday: joda.LocalDate;
  }[];
};

export function planWeekly(
  period: period,
  nextPeriod: null | period,
  teacher: {
    absences: {
      date: joda.LocalDate;
      time: joda.LocalTime;
      duration: number;
    }[];
  },
  dateSelectedByUser: joda.LocalDate,
  numberOfLessons: number,
  fromStartOfPeriod: boolean,
  slot: {
    time: joda.LocalTime;
    duration: number;
  },
  skipOtherDates?: joda.LocalDate[],
):
  | {
      lessons: joda.LocalDate[];
      lessonsSkipped: joda.LocalDate[];
      holidaysSkipped: { date: joda.LocalDate; description: string }[];
      otherDaysSkipped: joda.LocalDate[];
      teacherAbsencesSkipped: {
        date: joda.LocalDate;
      }[];
    }
  | { error: string } {
  const res = planWithRestLessons(
    period,
    nextPeriod,
    teacher,
    dateSelectedByUser,
    numberOfLessons,
    fromStartOfPeriod,
    slot,
    { tag: "weekly", skipOtherDates: skipOtherDates || [] },
  );
  if ("error" in res) {
    return res;
  } else {
    if (res.restLessons > 0) {
      return {
        error: "Slechts plaats in bestaande periodes voor %num lessen".replace("%num", res.lessons.length.toString()),
      };
    } else {
      return res;
    }
  }
}

export function planWithRestLessons(
  period: period,
  nextPeriod: null | period,
  teacher: {
    absences: {
      date: joda.LocalDate;
      time: joda.LocalTime;
      duration: number;
    }[];
  },
  dateSelectedByUser: joda.LocalDate,
  numberOfLessons: number,
  fromStartOfPeriod: boolean,
  slot: {
    time: joda.LocalTime;
    duration: number;
  },
  mode:
    | { tag: "weekly"; skipOtherDates: joda.LocalDate[] }
    | { tag: "biweekly"; complementLessons: joda.LocalDate[] },
):
  | {
      restLessons: number;
      lessons: joda.LocalDate[];
      lessonsSkipped: joda.LocalDate[];
      otherDaysSkipped: joda.LocalDate[];
      holidaysSkipped: { date: joda.LocalDate; description: string }[];
      teacherAbsencesSkipped: {
        date: joda.LocalDate;
      }[];
    }
  | { error: string } {
  const firstDate =
    fromStartOfPeriod === true
      ? getFirstWeekdayFrom(period.firstday, dateSelectedByUser.dayOfWeek())
      : dateSelectedByUser;
  const lessons = [];
  const lessonsSkipped = [];
  const holidaysSkipped = [];
  const otherDaysSkipped = [];
  const teacherAbsencesSkipped = [];
  const absoluteLastDate = nextPeriod ? nextPeriod.lastday : period.lastday;
  let currentDate = firstDate;
  let restLessons = 0;
  let biweeklySkipDone = true;

  const holidays = period.holidays.concat(nextPeriod?.holidays || []);

  while (
    lessons.length + lessonsSkipped.length < numberOfLessons &&
    currentDate.isBefore(absoluteLastDate)
  ) {
    const h = holidays.find(
      (h) =>
        h.firstday.isEqual(currentDate) ||
        h.lastday.isEqual(currentDate) ||
        (h.firstday.isBefore(currentDate) && h.lastday.isAfter(currentDate)),
    );
    if (h) {
      holidaysSkipped.push({ date: currentDate, description: h.description });
    } else if (currentDate.isBefore(dateSelectedByUser)) {
      // Before date selected by user -> skip and add to lessonsSkipped
      if (mode.tag === "weekly") {
        lessonsSkipped.push(currentDate);
      } else if (mode.tag === "biweekly") {
        if (biweeklySkipDone === true) {
          lessonsSkipped.push(currentDate);
          biweeklySkipDone = false;
        } else {
          // Biweekly plannings always need either a complement lesson or an open day in between two lessons
          // lessonsSkipped.push(currentDate);
          biweeklySkipDone = true;
        }
      } else {
        checkAllCasesHandled(mode);
      }
    } else if (
      (nextPeriod === null && currentDate.isAfter(period.lastday)) ||
      (nextPeriod !== null && currentDate.isAfter(nextPeriod.lastday))
    ) {
      // outside of period
      restLessons += 1;
    } else if (
      mode.tag === "biweekly" &&
      mode.complementLessons.some((d) => d.isEqual(currentDate))
    ) {
      // nothing, we just skip
      otherDaysSkipped.push(currentDate);
      biweeklySkipDone = true;
    } else if (
      mode.tag === "weekly" &&
      mode.skipOtherDates.some((d) => d.isEqual(currentDate))
    ) {
      // nothing, we just skip
      otherDaysSkipped.push(currentDate);
    } else {
      const absenceInTheWay = teacher.absences.find(
        (abs) => abs.date.isEqual(currentDate) && timerangesOverlap(abs, slot),
      );
      if (absenceInTheWay) {
        teacherAbsencesSkipped.push({ date: absenceInTheWay.date });
      } else {
        if (mode.tag === "weekly") {
          lessons.push(currentDate);
        } else if (mode.tag === "biweekly") {
          if (biweeklySkipDone === true) {
            lessons.push(currentDate);
            biweeklySkipDone = false;
          } else {
            // Biweekly plannings always need either a complement lesson or an open day in between two lessons
            // lessonsSkipped.push(currentDate);
            biweeklySkipDone = true;
          }
        } else {
          checkAllCasesHandled(mode);
        }
      }
    }
    currentDate = currentDate.plusDays(7);
    if (
      currentDate.isAfter(period.lastday) &&
      nextPeriod &&
      currentDate.isBefore(nextPeriod.firstday)
    ) {
      if (nextPeriod !== null) {
        currentDate = getFirstWeekdayFrom(
          nextPeriod.firstday,
          dateSelectedByUser.dayOfWeek(),
        );
      }
    }
  }

  return {
    lessons,
    lessonsSkipped,
    holidaysSkipped,
    teacherAbsencesSkipped,
    otherDaysSkipped,
    restLessons,
  };
}

export function planBiweekly(
  period: period,
  nextPeriod: null | period,
  teacher: {
    absences: {
      date: joda.LocalDate;
      time: joda.LocalTime;
      duration: number;
    }[];
  },
  dateSelectedByUser: joda.LocalDate,
  numberOfLessons: number,
  fromStartOfPeriod: boolean,
  slot: {
    time: joda.LocalTime;
    duration: number;
  },
  complement: null | joda.LocalDate[],
):
  | {
      lessons: joda.LocalDate[];
      lessonsSkipped: joda.LocalDate[];
      holidaysSkipped: { date: joda.LocalDate; description: string }[];
      complementLessonsSkipped: joda.LocalDate[];
      teacherAbsencesSkipped: {
        date: joda.LocalDate;
      }[];
    }
  | { error: string } {
  const res = planWithRestLessons(
    period,
    nextPeriod,
    teacher,
    dateSelectedByUser,
    numberOfLessons,
    fromStartOfPeriod,
    slot,
    { tag: "biweekly", complementLessons: complement || [] },
  );
  if ("error" in res) {
    return res;
  } else {
    if (res.restLessons > 0) {
      return {
        error: "Slechts plaats in bestaande periodes voor %num lessen".replace("%num", res.lessons.length.toString()),
      };
    } else {
      return {
        lessons: res.lessons,
        lessonsSkipped: res.lessonsSkipped,
        holidaysSkipped: res.holidaysSkipped,
        teacherAbsencesSkipped: res.teacherAbsencesSkipped,
        complementLessonsSkipped: res.otherDaysSkipped,
      };
    }
  }
}
